import { cn } from "@gigpro/ui/tailwind/cn";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import React from "react";
import ReactDOM from "react-dom";
import { Button } from "../Button";
import { Checkbox, CheckboxGroup } from "../Checkbox";
import { Dialog, DialogFooter, DialogTitle } from "../Dialog";
import { Label } from "../Label";
import { RadioGroup, RadioGroupItem } from "../RadioGroup";
import { SelectLabel, SelectRoot, SelectTrigger, type SelectTriggerProps } from "../Select";

type DialogSelectValueProps =
  | {
      mode: "single";
      value: string;
      onValueChange: (value: string) => void;
    }
  | {
      mode: "multiple";
      value: Array<string>;
      onValueChange: (value: Array<string>) => void;
    };

type DialogSelectContext = {
  open: boolean;
  setOpen: (open: boolean, apply?: boolean) => void;

  valueNodeMounted: boolean;
  setValueNodeMounted: (mounted: boolean) => void;
  textValueLookup: Map<string, string>;
  setTextValue: (value: string, textValue: string) => void;
} & DialogSelectValueProps;
const DialogSelectContext = React.createContext<DialogSelectContext>({
  open: false,
  setOpen: () => {},
  mode: "single",
  value: "",
  onValueChange: () => {},

  valueNodeMounted: false,
  setValueNodeMounted: () => {},
  textValueLookup: new Map(),
  setTextValue: () => {},
});

type DialogSelectRootProps = DialogSelectValueProps & {
  children: React.ReactNode;
};

export function DialogSelectRoot({ mode, value, onValueChange, children }: Readonly<DialogSelectRootProps>) {
  const [open, setOpen] = React.useState(false);

  const [initialValue, setInitialValue] = React.useState(value);
  const [internalValue, setInternalValue] = React.useState(value);
  const [prev, setPrev] = React.useState({ open, value });
  if (prev.open !== open || prev.value !== value) {
    setPrev({ open, value });
    setInternalValue(value);
    if (!open) setInitialValue(value);
  }

  const [valueNodeMounted, setValueNodeMounted] = React.useState(false);
  const [textValueLookup, setTextValueLookup] = React.useState(() => new Map<string, string>());

  const contextValue: DialogSelectContext = {
    open,
    setOpen: (open, apply) => {
      if (!open && !apply) {
        // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
        onValueChange(initialValue as any);
      }
      setOpen(open);
    },
    // the discriminated union doesn't survive the destructuring, gotta type-cast it back in
    ...({ mode, value: internalValue } as DialogSelectValueProps),
    onValueChange: (value: string | Array<string>) => {
      setInternalValue(value);
      // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
      React.startTransition(() => onValueChange(value as any));
    },

    valueNodeMounted,
    setValueNodeMounted,
    textValueLookup,
    setTextValue: (value, textValue) =>
      setTextValueLookup((prev) => (prev.get(value) === textValue ? prev : new Map(prev).set(value, textValue))),
  };

  return (
    <DialogSelectContext.Provider value={contextValue}>
      {/* provide a value to the placeholder styling works */}
      <SelectRoot open={open} onOpenChange={setOpen} value={Array.isArray(value) ? value.join(", ") : value}>
        {children}
      </SelectRoot>
    </DialogSelectContext.Provider>
  );
}

export interface DialogSelectProps extends DialogSelectTriggerProps {
  dialogProps: Omit<DialogSelectDialogProps, "children">;
}
const DialogSelect = ({
  ref,
  children,
  mode,
  value,
  onValueChange,
  dialogProps,
  ...props
}: DialogSelectProps & DialogSelectValueProps) => (
  <DialogSelectRoot {...({ mode, value, onValueChange } as DialogSelectValueProps)}>
    <DialogSelectTrigger ref={ref} {...props} />
    <DialogSelectDialog {...dialogProps}>{children}</DialogSelectDialog>
  </DialogSelectRoot>
);

export interface DialogSelectTriggerProps extends SelectTriggerProps {
  label: React.ReactNode;
  placeholder?: React.ReactNode;

  error?: string;
}
export const DialogSelectTrigger = ({
  ref,
  label,
  placeholder,
  className,
  error,
  ...props
}: DialogSelectTriggerProps) => {
  const id = React.useId();
  return (
    <div className={className}>
      <SelectLabel htmlFor={id} className={cn(error ? "text-error" : undefined)}>
        {label}
      </SelectLabel>
      <SelectTrigger ref={ref} {...props} id={id} className={cn(error ? "border-error" : undefined)}>
        <DialogSelectValue placeholder={placeholder} />
      </SelectTrigger>
      {error && <p className="font-semibold text-error">{error}</p>}
    </div>
  );
};

function shouldShowPlaceholder(value?: string | Array<string>) {
  return value === "" || value === undefined || (Array.isArray(value) && !value.length);
}
function isSelected(value: string | Array<string>, check: string) {
  if (Array.isArray(value)) return value.includes(check);
  return value === check;
}

interface SelectValueProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "placeholder"> {
  ref?: React.Ref<HTMLSpanElement>;
  placeholder?: React.ReactNode;
}
const DialogSelectValue = ({
  ref: forwardedRef,
  className,
  style,
  children,
  placeholder = "",
  ...valueProps
}: SelectValueProps) => {
  const { value, textValueLookup, setValueNodeMounted } = React.useContext(DialogSelectContext);

  React.useEffect(() => {
    setValueNodeMounted(true);
    return () => setValueNodeMounted(false);
  }, [setValueNodeMounted]);

  return (
    <span
      {...valueProps}
      ref={forwardedRef}
      // we don't want events from the portalled `SelectValue` children to bubble
      // through the item they came from
      style={{ pointerEvents: "none" }}
    >
      {shouldShowPlaceholder(value) ? (
        // oxlint-disable-next-line react/jsx-no-useless-fragment -- fragment narrows the node to a valid JSX return
        <>{placeholder}</>
      ) : (
        (children ??
        [...textValueLookup.entries()]
          .filter(([v]) => isSelected(value, v))
          .map(([, label]) => label)
          .join(", "))
      )}
    </span>
  );
};
DialogSelectValue.displayName = "DialogSelectValue";

type DialogSelectDialogProps = {
  title: React.ReactNode;
  action?: React.ReactNode;
  onAction?: () => void;
  children: React.ReactNode;
};
function DialogSelectDialog({ title, action, onAction, children }: Readonly<DialogSelectDialogProps>) {
  const { open, setOpen, mode, value, onValueChange, valueNodeMounted } = React.useContext(DialogSelectContext);
  const [fragment] = React.useState(() => new DocumentFragment());

  const contentClassNames = "max-h-64 overflow-auto [&>p]:sticky [&>p]:top-0 [&>p]:z-10 [&>p]:bg-white [&>p]:pb-1";
  const selectGroup =
    mode === "single" ? (
      <RadioGroup value={value} onValueChange={onValueChange} className={contentClassNames}>
        {children}
      </RadioGroup>
    ) : (
      <CheckboxGroup value={value} onValueChange={onValueChange} className={contentClassNames}>
        {children}
      </CheckboxGroup>
    );

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen} bottomSheet closeOnBackdropClick>
        {typeof title === "string" ? <DialogTitle>{title}</DialogTitle> : title}
        {action && onAction && (
          <Button variant="outlined" onClick={onAction}>
            {action}
          </Button>
        )}

        {selectGroup}

        <DialogFooter>
          <Button size="small" variant="contained" onClick={() => setOpen(false, true)}>
            Apply
          </Button>
        </DialogFooter>
      </Dialog>
      {/* render in a fragment (an invisible node)
          so the items are rendered even if the dialog is closed
          to make textValue population work */}
      {!open && valueNodeMounted && ReactDOM.createPortal(selectGroup, fragment)}
    </>
  );
}

const dialogSelectItemClassNames =
  "flex flex-1 min-w-0 items-center gap-2 rounded scroll-mt-7 bg-background px-2 text-brand";
const dialogSelectItemLabelClassNames = "w-full py-2 text-normal font-semibold truncate aria-disabled:text-gray";

interface DialogSelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
  value: string;
  label?: string;
  action?: React.ReactNode;
  disabled?: boolean;
}
const DialogSelectItem = ({
  ref: forwardedRef,
  className,
  children,
  value,
  label,
  action,
  disabled = false,
  ...props
}: DialogSelectItemProps) => {
  const id = React.useId();
  const context = React.useContext(DialogSelectContext);
  const { mode, setTextValue: setLookupTextValue } = context;

  const [textValue, setTextValue] = React.useState(label ?? "");
  const innerRef = React.useRef<HTMLDivElement>(null);
  const ref = useComposedRefs(forwardedRef, innerRef, (node) => {
    if (label) return;
    setTextValue((node?.textContent ?? "").trim());
  });

  React.useEffect(() => {
    if (!textValue) return;
    setLookupTextValue(value, textValue);
  }, [textValue, setLookupTextValue, value]);

  const scrollIntoViewIfSelected = React.useEffectEvent(() => {
    if (isSelected(context.value, value)) innerRef.current?.scrollIntoView({ block: "center" });
  });
  React.useEffect(() => {
    if (mode === "single") scrollIntoViewIfSelected();
  }, [mode]);

  return (
    <div ref={ref} className={cn(dialogSelectItemClassNames, className)} {...props}>
      {mode === "single" ? (
        <RadioGroupItem id={id} value={value} disabled={disabled} />
      ) : (
        <Checkbox id={id} value={value} disabled={disabled} />
      )}
      <Label htmlFor={id} aria-disabled={disabled} className={dialogSelectItemLabelClassNames}>
        {children}
      </Label>
      {action}
    </div>
  );
};

export { SelectBaseTrigger as DialogSelectBaseTrigger } from "../Select";
export { DialogSelectDialog, DialogSelectItem, DialogSelect };
